import { useEffect, useState } from "react";
import API from "@/lib/api";
import { Compass, RefreshCw, AlertTriangle } from "lucide-react";
import HelpLink from "@/components/HelpLink";

/**
 * Admin Overview tile showing patient first-visit tour completion stats.
 *
 * - completion_rate = completed / (completed + skipped)
 * - engagement_rate = (completed + replayed) / started  (broader engagement)
 * - per_step_advance reveals which step is the biggest drop-off cliff
 *
 * Hides itself when there is zero data (no tours started yet) — keeps the
 * Overview tab clean for fresh installs.
 */
export default function PatientTourStatsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    API.get("/admin/patient-tour-stats?days=30")
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (loading) return null;
  if (!data || (data.started || 0) === 0) return null;

  const cRate = data.completion_rate || 0;
  const eRate = data.engagement_rate || 0;
  const stepKeys = ["Account #", "Statuses", "Receipts", "Subs"];
  const alertActive = data?.alert?.active;
  const last7 = data?.last_7d || {};

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5" data-testid="patient-tour-stats-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <Compass className="w-4 h-4 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white text-sm inline-flex items-center gap-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Patient welcome tour
              <HelpLink sectionId="patient-tour-stats" title="Training: how to read tour stats" size={11} />
            </h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Last 30 days · {data.started} new patients saw the tour</p>
          </div>
        </div>
        <button type="button" onClick={load} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-700" aria-label="Refresh" data-testid="tour-stats-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 dark:text-emerald-300">Completion rate</p>
          <p className="text-2xl font-bold tabular-nums text-emerald-700 dark:text-emerald-300" data-testid="tour-completion-rate">
            {cRate}%
          </p>
          <p className="text-[10px] text-slate-500">{data.completed}/{data.completed + data.skipped} closed tours</p>
        </div>
        <div className="rounded-lg bg-indigo-50 dark:bg-indigo-950/30 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-indigo-700 dark:text-indigo-300">Engagement</p>
          <p className="text-2xl font-bold tabular-nums text-indigo-700 dark:text-indigo-300" data-testid="tour-engagement-rate">
            {eRate}%
          </p>
          <p className="text-[10px] text-slate-500">{data.completed + data.replayed}/{data.started} (incl. replays)</p>
        </div>
      </div>

      {/* Low-completion-rate alert.  Fires only when the *last 7-day* window
          has >= 5 closed tours AND completion rate is < 50%.  Same data
          drives the weekly admin email; backend gates both. */}
      {alertActive && (
        <div
          className="rounded-lg border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 px-3 py-2.5 mb-3 flex items-start gap-2 text-xs"
          role="alert"
          data-testid="tour-completion-alert"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold" data-testid="tour-completion-alert-summary">
              Tour completion dropped to {last7.completion_rate || 0}% in the last 7 days
            </p>
            <p className="text-[11px] opacity-80 mt-0.5">
              <span className="tabular-nums font-mono">{last7.completed || 0}</span> completed · <span className="tabular-nums font-mono">{last7.skipped || 0}</span> skipped.
              Check the step funnel below to spot the drop-off cliff, or pull up a session replay of a recent skipper to see where they bailed.
            </p>
          </div>
        </div>
      )}

      {/* Per-step advance funnel */}
      <div className="border-t border-slate-200 dark:border-slate-700 pt-2.5">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold mb-1.5">
          Step funnel
        </p>
        <div className="flex items-end gap-1.5 h-12">
          {stepKeys.map((label, i) => {
            const count = data.per_step_advance?.[i] || 0;
            const max = Math.max(1, ...Object.values(data.per_step_advance || {0:1}));
            const heightPct = (count / max) * 100;
            return (
              <div key={i} className="flex-1 flex flex-col items-center h-full" data-testid={`tour-step-${i}`}>
                <div className="w-full flex-1 bg-slate-100 dark:bg-slate-800 rounded relative overflow-hidden">
                  <div
                    className="absolute bottom-0 left-0 right-0 bg-emerald-500 transition-all"
                    style={{ height: count > 0 ? `${Math.max(heightPct, 8)}%` : "0%" }}
                    title={`${count} advances past ${label}`}
                  />
                </div>
                <span className="text-[9px] text-slate-500 mt-1 tabular-nums">{count}</span>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-[9px] text-slate-400 mt-0.5">
          {stepKeys.map((label, i) => (
            <span key={i} className="flex-1 text-center">{label}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
